import React, { useState, useEffect } from 'react';
import {
  Presentation,
  FileText,
  Palette,
  Layout,
  Sparkles,
  Download,
  Eye,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
  Image,
  BarChart3,
  Edit3,
  Trash2,
  Zap,
  AlertCircle
} from 'lucide-react';
import CompanyDataAuthorizationPanel from '../components/CompanyDataAuthorizationPanel';
import {
  isAuthorizationRequiredError,
  readApiResponseError,
} from '../services/companyDataAuthorizationService';

type Step = 'select-proposal' | 'select-template' | 'customize' | 'generate' | 'preview';

interface SlideData {
  id: number;
  type: string;
  title: string;
  content: string;
  category?: string;
  bullets?: string[];
  chart_data?: {
    type: string;
    title: string;
    categories: string[];
    series?: { name: string; values: number[] }[];
    values?: number[];
    unit?: string;
  };
  design?: {
    colors: {
      primary: string;
      secondary: string;
      accent: string;
      text: string;
      background: string;
    };
    layout: {
      layout: string;
      title_size: number;
      body_size?: number;
    };
  };
}

interface PPTData {
  id: string;
  title: string;
  proposal_id: string;
  company_id: string;
  authorization_id: string;
  template: string;
  template_name: string;
  color_scheme: string;
  color_scheme_name: string;
  slide_count: number;
  slides: SlideData[];
  generated_at: string;
  ai_model: string;
}

interface ProposalListItem {
  id: string;
  title: string;
  grant: string;
  company: string;
  company_id: string;
  status: string;
  created_at?: string;
}

const getApiBase = (): string => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `${protocol}//${hostname}:8001/api`;
  }
  return 'http://localhost:8001/api';
};

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('proposalcraft_access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const PPTGenerator: React.FC = () => {
  const API_BASE = getApiBase();
  const progressIntervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const [currentStep, setCurrentStep] = useState<Step>('select-proposal');
  const [selectedProposal, setSelectedProposal] = useState<string | null>(null);
  const [selectedAuthorizationId, setSelectedAuthorizationId] =
    useState<string | null>(null);
  const [authorizationRevision, setAuthorizationRevision] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('standard');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pptData, setPptData] = useState<PPTData | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingSlide, setEditingSlide] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');

  const [templates, setTemplates] = useState<any[]>([]);
  const [colorSchemes, setColorSchemes] = useState<any[]>([]);

  const [customOptions, setCustomOptions] = useState({
    includeCharts: true,
    includeImages: true,
    colorScheme: 'blue',
    fontStyle: 'modern',
  });

  const [proposals, setProposals] = useState<ProposalListItem[]>([]);
  const [proposalsLoading, setProposalsLoading] = useState(true);
  const [proposalsError, setProposalsError] = useState<string | null>(null);
  const selectedProposalData =
    proposals.find((proposal) => proposal.id === selectedProposal) || null;
  const selectedCompanyId = selectedProposalData?.company_id || null;

  // 실제 제안서 목록 API에서 가져오기
  useEffect(() => {
    const fetchProposals = async () => {
      setProposalsLoading(true);
      setProposalsError(null);
      try {
        const response = await fetch(`${API_BASE}/proposals?limit=50`, {
          headers: getAuthHeaders(),
        });

        if (!response.ok) throw new Error(`서버 오류 (${response.status})`);

        const data = await response.json();
        // 백엔드는 ProposalResponse 배열을 직접 반환
        const items = Array.isArray(data) ? data : (data.items || data.proposals || []);

        setProposals(items.map((p: any) => ({
          id: p.id || p.proposal_id,
          title: p.title,
          grant: p.grant_title || '',
          company: p.company_name || '',
          company_id: p.company_id || '',
          status: p.status || 'draft',
          created_at: p.created_at,
        })));
      } catch (err: any) {
        console.error('Failed to fetch proposals:', err);
        setProposalsError('제안서 목록을 불러오지 못했습니다. 서버 연결을 확인해주세요.');
        setProposals([]);
      } finally {
        setProposalsLoading(false);
      }
    };

    fetchProposals();
  }, [API_BASE]);

  // 템플릿 및 색상 스킴 로드
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const headers = getAuthHeaders();
        const [templatesRes, colorsRes] = await Promise.all([
          fetch(`${API_BASE}/ppt/templates`, { headers }),
          fetch(`${API_BASE}/ppt/color-schemes`, { headers })
        ]);
        
        if (templatesRes.ok) {
          const templatesData = await templatesRes.json();
          setTemplates(templatesData);
        } else {
          // 기본 템플릿 사용
          setTemplates([
            { id: 'standard', name: '공모사업 표준', description: '정부 공모사업 발표용 표준 템플릿', slides: 15 },
            { id: 'technical', name: '기술 제안', description: '기술 중심의 현대적인 디자인', slides: 12 },
            { id: 'business', name: '비즈니스 프로', description: '전문적인 비즈니스 프레젠테이션', slides: 18 },
            { id: 'simple', name: '간단 발표', description: '깔끔하고 간결한 디자인', slides: 8 },
          ]);
        }
        
        if (colorsRes.ok) {
          const colorsData = await colorsRes.json();
          setColorSchemes(colorsData);
        } else {
          // 기본 색상 스킴 사용
          setColorSchemes([
            { id: 'blue', name: '블루', primary: '#3B82F6', secondary: '#1E40AF' },
            { id: 'green', name: '그린', primary: '#10B981', secondary: '#047857' },
            { id: 'purple', name: '퍼플', primary: '#8B5CF6', secondary: '#5B21B6' },
            { id: 'orange', name: '오렌지', primary: '#F97316', secondary: '#C2410C' },
            { id: 'dark', name: '다크', primary: '#60A5FA', secondary: '#3B82F6' },
          ]);
        }
      } catch (err) {
        console.error('Failed to load options:', err);
        // 기본값 설정
        setTemplates([
          { id: 'standard', name: '공모사업 표준', description: '정부 공모사업 발표용 표준 템플릿', slides: 15 },
          { id: 'technical', name: '기술 제안', description: '기술 중심의 현대적인 디자인', slides: 12 },
          { id: 'business', name: '비즈니스 프로', description: '전문적인 비즈니스 프레젠테이션', slides: 18 },
          { id: 'simple', name: '간단 발표', description: '깔끔하고 간결한 디자인', slides: 8 },
        ]);
        setColorSchemes([
          { id: 'blue', name: '블루', primary: '#3B82F6', secondary: '#1E40AF' },
          { id: 'green', name: '그린', primary: '#10B981', secondary: '#047857' },
          { id: 'purple', name: '퍼플', primary: '#8B5CF6', secondary: '#5B21B6' },
          { id: 'orange', name: '오렌지', primary: '#F97316', secondary: '#C2410C' },
          { id: 'dark', name: '다크', primary: '#60A5FA', secondary: '#3B82F6' },
        ]);
      }
    };
    
    loadOptions();
  }, []);

  const steps = [
    { id: 'select-proposal', label: '제안서 선택', icon: FileText },
    { id: 'select-template', label: '템플릿 선택', icon: Layout },
    { id: 'customize', label: '커스터마이즈', icon: Palette },
    { id: 'generate', label: 'PPT 생성', icon: Sparkles },
    { id: 'preview', label: '미리보기', icon: Eye },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const handleNext = () => {
    const stepOrder: Step[] = ['select-proposal', 'select-template', 'customize', 'generate', 'preview'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const handlePrev = () => {
    const stepOrder: Step[] = ['select-proposal', 'select-template', 'customize', 'generate', 'preview'];
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const handleGenerate = async () => {
    if (
      !selectedProposal ||
      !selectedProposalData ||
      !selectedAuthorizationId
    ) {
      setError('제안서와 활성 기업 자료 처리 동의를 먼저 선택해 주세요.');
      setCurrentStep('select-proposal');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setError(null);

    // 진행률 시뮬레이션
    progressIntervalRef.current = setInterval(() => {
      setGenerationProgress(prev => {
        if (prev >= 90) {
          if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
          return 90;
        }
        return prev + 10;
      });
    }, 300);

    try {
      const response = await fetch(`${API_BASE}/ppt/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          proposal_id: selectedProposal,
          company_id: selectedProposalData.company_id,
          authorization_id: selectedAuthorizationId,
          template_id: selectedTemplate,
          color_scheme: customOptions.colorScheme,
          include_charts: customOptions.includeCharts,
          include_images: customOptions.includeImages,
        }),
      });

      if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
      setGenerationProgress(100);

      if (!response.ok) {
        const apiError = await readApiResponseError(
          response,
          'PPT 생성에 실패했습니다.',
        );
        if (isAuthorizationRequiredError(apiError)) {
          setSelectedAuthorizationId(null);
          setAuthorizationRevision((current) => current + 1);
          setCurrentStep('select-proposal');
        }
        throw new Error(apiError.message);
      }

      const data = await response.json();
      setPptData(data);

      setTimeout(() => {
        setIsGenerating(false);
        handleNext();
      }, 500);

    } catch (err) {
      if (progressIntervalRef.current) { clearInterval(progressIntervalRef.current); progressIntervalRef.current = null; }
      setError(err instanceof Error ? err.message : 'PPT 생성 중 오류가 발생했습니다');
      setIsGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (
      !pptData ||
      !selectedProposal ||
      !selectedProposalData ||
      !selectedAuthorizationId
    ) {
      setError('다운로드 전에 제안서와 활성 동의를 다시 확인해 주세요.');
      setCurrentStep('select-proposal');
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/ppt/download`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          proposal_id: selectedProposal,
          company_id: selectedProposalData.company_id,
          authorization_id: selectedAuthorizationId,
          ppt_data: pptData,
        }),
      });

      if (!response.ok) {
        const apiError = await readApiResponseError(
          response,
          '다운로드에 실패했습니다.',
        );
        if (isAuthorizationRequiredError(apiError)) {
          setSelectedAuthorizationId(null);
          setAuthorizationRevision((current) => current + 1);
          setCurrentStep('select-proposal');
        }
        throw new Error(apiError.message);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pptData.title.replace(/[^a-zA-Z0-9가-힣]/g, '_')}.pptx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : '다운로드 중 오류가 발생했습니다');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEditSlide = (slideIndex: number) => {
    if (pptData) {
      setEditingSlide(slideIndex);
      setEditContent(pptData.slides[slideIndex].content);
    }
  };

  const handleSaveEdit = () => {
    if (pptData && editingSlide !== null) {
      const updatedSlides = [...pptData.slides];
      updatedSlides[editingSlide] = {
        ...updatedSlides[editingSlide],
        content: editContent,
      };
      setPptData({ ...pptData, slides: updatedSlides });
      setEditingSlide(null);
    }
  };

  const handleDeleteSlide = (slideIndex: number) => {
    if (pptData && pptData.slides.length > 1) {
      const updatedSlides = pptData.slides.filter((_, index) => index !== slideIndex);
      setPptData({ ...pptData, slides: updatedSlides, slide_count: updatedSlides.length });
      if (currentSlide >= updatedSlides.length) {
        setCurrentSlide(updatedSlides.length - 1);
      }
    }
  };

  const getSlideTypeIcon = (type: string) => {
    switch (type) {
      case 'chart': return <BarChart3 className="h-4 w-4" />;
      case 'image': return <Image className="h-4 w-4" />;
      case 'title': return <Presentation className="h-4 w-4" />;
      case 'closing': return <FileText className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getTemplateColor = (templateId: string) => {
    const colors: { [key: string]: string } = {
      standard: 'from-blue-500 to-blue-600',
      technical: 'from-emerald-500 to-teal-600',
      business: 'from-purple-500 to-indigo-600',
      simple: 'from-gray-500 to-gray-600',
    };
    return colors[templateId] || colors.standard;
  };

  const getSlideBackground = () => {
    const scheme = colorSchemes.find(s => s.id === customOptions.colorScheme);
    if (scheme) {
      return `linear-gradient(135deg, ${scheme.primary}, ${scheme.secondary})`;
    }
    return 'linear-gradient(135deg, #3B82F6, #1E40AF)';
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">PPT 자동 생성</h1>
          <p className="text-gray-600 mt-2">
            저장된 제안서를 권한과 동의 범위 안에서 프레젠테이션으로 변환합니다.
          </p>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button 
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* 진행 단계 표시 */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = index < currentStepIndex;
            
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    isActive ? 'bg-emerald-600 text-white' :
                    isCompleted ? 'bg-emerald-100 text-emerald-600' :
                    'bg-gray-100 text-gray-400'
                  }`}>
                    {isCompleted ? <Check className="h-6 w-6" /> : <Icon className="h-6 w-6" />}
                  </div>
                  <span className={`mt-2 text-sm font-medium ${
                    isActive ? 'text-emerald-600' : isCompleted ? 'text-emerald-600' : 'text-gray-400'
                  }`}>
                    {step.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-4 rounded ${
                    index < currentStepIndex ? 'bg-emerald-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* 단계별 콘텐츠 */}
      <div className="card min-h-[500px]">
        {/* 제안서 선택 */}
        {currentStep === 'select-proposal' && (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <FileText className="h-6 w-6 text-emerald-600" />
              <h2 className="text-xl font-semibold text-gray-900">제안서 선택</h2>
            </div>
            <p className="text-gray-600">PPT로 변환할 제안서를 선택하세요.</p>

            {proposalsLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" />
                <span className="ml-2 text-gray-600">제안서 목록 로딩 중...</span>
              </div>
            )}

            {proposalsError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{proposalsError}</span>
              </div>
            )}

            {!proposalsLoading && !proposalsError && proposals.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                <p>작성된 제안서가 없습니다. 먼저 제안서를 작성해주세요.</p>
              </div>
            )}

            <div className="space-y-3">
              {proposals.map((proposal) => (
                <div
                  key={proposal.id}
                  onClick={() => {
                    setSelectedProposal(proposal.id);
                    setPptData(null);
                    setError(null);
                  }}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    selectedProposal === proposal.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center text-white">
                        <FileText className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{proposal.title}</h3>
                        <p className="text-sm text-gray-500">{proposal.grant} · {proposal.company}</p>
                      </div>
                    </div>
                    {selectedProposal === proposal.id && (
                      <Check className="h-6 w-6 text-emerald-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {selectedCompanyId && (
              <CompanyDataAuthorizationPanel
                key={`${selectedCompanyId}:${authorizationRevision}`}
                companyId={selectedCompanyId}
                value={selectedAuthorizationId}
                onChange={setSelectedAuthorizationId}
              />
            )}
          </div>
        )}

        {/* 템플릿 선택 */}
        {currentStep === 'select-template' && (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <Layout className="h-6 w-6 text-emerald-600" />
              <h2 className="text-xl font-semibold text-gray-900">템플릿 선택</h2>
            </div>
            <p className="text-gray-600">PPT 디자인 템플릿을 선택하세요.</p>
            
            <div className="grid grid-cols-2 gap-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    selectedTemplate === template.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`h-32 bg-gradient-to-br ${getTemplateColor(template.id)} rounded-lg mb-4 flex items-center justify-center`}>
                    <Presentation className="h-12 w-12 text-white opacity-50" />
                  </div>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      <p className="text-sm text-gray-500">{template.description}</p>
                      <p className="text-xs text-gray-400 mt-1">{template.slides}슬라이드</p>
                    </div>
                    {selectedTemplate === template.id && (
                      <Check className="h-5 w-5 text-emerald-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 커스터마이즈 */}
        {currentStep === 'customize' && (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <Palette className="h-6 w-6 text-emerald-600" />
              <h2 className="text-xl font-semibold text-gray-900">커스터마이즈</h2>
            </div>
            <p className="text-gray-600">PPT 생성 옵션을 설정하세요.</p>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">색상 테마</label>
                  <div className="flex flex-wrap gap-3">
                    {colorSchemes.map((scheme) => (
                      <button
                        key={scheme.id}
                        onClick={() => setCustomOptions({...customOptions, colorScheme: scheme.id})}
                        className={`flex flex-col items-center p-3 border-2 rounded-lg transition-all ${
                          customOptions.colorScheme === scheme.id
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex space-x-1 mb-2">
                          <div
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: scheme.primary }}
                          />
                          <div
                            className="w-6 h-6 rounded-full"
                            style={{ backgroundColor: scheme.secondary }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">{scheme.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">선택된 템플릿</label>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-900">
                      {templates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}
                    </p>
                    <p className="text-sm text-gray-500">
                      {templates.find(t => t.id === selectedTemplate)?.slides || 15}슬라이드
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">포함 요소</label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={customOptions.includeCharts}
                        onChange={(e) => setCustomOptions({...customOptions, includeCharts: e.target.checked})}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <BarChart3 className="h-5 w-5 text-gray-500" />
                      <span className="text-gray-700">차트/그래프 포함</span>
                    </label>
                    <label className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={customOptions.includeImages}
                        onChange={(e) => setCustomOptions({...customOptions, includeImages: e.target.checked})}
                        className="w-4 h-4 text-emerald-600 rounded"
                      />
                      <Image className="h-5 w-5 text-gray-500" />
                      <span className="text-gray-700">이미지/인포그래픽 포함</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">폰트 스타일</label>
                  <select
                    value={customOptions.fontStyle}
                    onChange={(e) => setCustomOptions({...customOptions, fontStyle: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="modern">모던 (Pretendard)</option>
                    <option value="classic">클래식 (나눔고딕)</option>
                    <option value="minimal">미니멀 (Noto Sans)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PPT 생성 */}
        {currentStep === 'generate' && (
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <Sparkles className="h-6 w-6 text-emerald-600" />
              <h2 className="text-xl font-semibold text-gray-900">PPT 생성</h2>
            </div>
            
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-8 text-center">
              {isGenerating ? (
                <div className="space-y-4">
                  <Loader2 className="h-16 w-16 text-emerald-600 mx-auto animate-spin" />
                  <h3 className="text-xl font-semibold text-gray-900">제안서를 PPT로 구성하고 있습니다...</h3>
                  <p className="text-gray-600">동의된 제안서 내용을 분석하고 슬라이드를 디자인합니다</p>
                  <div className="max-w-md mx-auto">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-600">생성 진행률</span>
                      <span className="font-medium text-emerald-600">{generationProgress}%</span>
                    </div>
                    <div className="w-full bg-emerald-200 rounded-full h-3">
                      <div 
                        className="bg-emerald-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${generationProgress}%` }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl mx-auto flex items-center justify-center">
                    <Zap className="h-10 w-10 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">PPT 생성 준비 완료</h3>
                  <p className="text-gray-600 max-w-md mx-auto">
                    저장된 제안서로 전문적인 프레젠테이션을 생성합니다.<br />
                    외부 AI는 승인된 처리자와 별도 동의가 준비된 경우에만 사용됩니다.
                  </p>
                  
                  <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-6">
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-sm text-gray-500">사용 API</p>
                      <p className="font-semibold text-gray-900">승인된 생성 엔진</p>
                    </div>
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-sm text-gray-500">템플릿</p>
                      <p className="font-semibold text-gray-900">
                        {templates.find(t => t.id === selectedTemplate)?.name || selectedTemplate}
                      </p>
                    </div>
                    <div className="p-3 bg-white rounded-lg">
                      <p className="text-sm text-gray-500">예상 시간</p>
                      <p className="font-semibold text-gray-900">약 30초</p>
                    </div>
                  </div>

                  <button 
                    onClick={handleGenerate}
                    disabled={!selectedAuthorizationId}
                    className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Sparkles className="h-5 w-5 mr-2 inline" />
                    PPT 생성하기
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 미리보기 */}
        {currentStep === 'preview' && pptData && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Eye className="h-6 w-6 text-emerald-600" />
                <h2 className="text-xl font-semibold text-gray-900">PPT 미리보기</h2>
                <span className="text-sm text-gray-500">
                  ({pptData.slide_count}슬라이드 · {pptData.ai_model})
                </span>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={() => {
                    setCurrentStep('generate');
                    setPptData(null);
                  }}
                  className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  재생성
                </button>
                <button 
                  onClick={handleDownload}
                  disabled={isDownloading || !selectedAuthorizationId}
                  className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  {isDownloading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  다운로드 (.pptx)
                </button>
              </div>
            </div>
            
            <div className="flex gap-6">
              {/* 슬라이드 목록 */}
              <div className="w-52 space-y-2 max-h-[500px] overflow-y-auto pr-2">
                {pptData.slides.map((slide, index) => (
                  <div
                    key={slide.id}
                    onClick={() => setCurrentSlide(index)}
                    className={`group p-2 border rounded-lg cursor-pointer transition-all relative ${
                      currentSlide === index
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div 
                      className="aspect-video rounded flex items-center justify-center text-white text-xs mb-1"
                      style={{ background: getSlideBackground() }}
                    >
                      {index + 1}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1 flex-1 min-w-0">
                        {getSlideTypeIcon(slide.type)}
                        <span className="text-xs text-gray-600 truncate">{slide.title}</span>
                      </div>
                      <div className="hidden group-hover:flex items-center space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSlide(index);
                          }}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <Edit3 className="h-3 w-3 text-gray-500" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSlide(index);
                          }}
                          className="p-1 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 슬라이드 미리보기 */}
              <div className="flex-1">
                {editingSlide !== null ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-gray-900">슬라이드 편집</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingSlide(null)}
                          className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded"
                        >
                          취소
                        </button>
                        <button
                          onClick={handleSaveEdit}
                          className="px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
                        >
                          저장
                        </button>
                      </div>
                    </div>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                ) : (
                  <>
                    <div
                      className="aspect-video rounded-xl shadow-lg p-8 text-white"
                      style={{ background: getSlideBackground() }}
                    >
                      <div className="h-full flex flex-col justify-center">
                        <h3 className="text-2xl font-bold mb-4">{pptData.slides[currentSlide]?.title}</h3>
                        <pre className="whitespace-pre-wrap text-sm opacity-90 font-sans">
                          {pptData.slides[currentSlide]?.content}
                        </pre>
                      </div>
                    </div>

                    {pptData.slides[currentSlide]?.chart_data && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <BarChart3 className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800">
                            차트: {pptData.slides[currentSlide].chart_data!.type === 'bar' ? '막대 그래프' :
                                   pptData.slides[currentSlide].chart_data!.type === 'line' ? '선 그래프' :
                                   pptData.slides[currentSlide].chart_data!.type === 'pie' ? '원형 그래프' :
                                   pptData.slides[currentSlide].chart_data!.type}
                          </span>
                        </div>
                        {pptData.slides[currentSlide].chart_data!.categories && (
                          <div className="text-xs text-blue-600">
                            항목: {pptData.slides[currentSlide].chart_data!.categories.join(', ')}
                          </div>
                        )}
                        {pptData.slides[currentSlide].chart_data!.series && (
                          <div className="text-xs text-blue-600 mt-1">
                            데이터: {pptData.slides[currentSlide].chart_data!.series!.map((s: any) => s.name).join(', ')}
                          </div>
                        )}
                      </div>
                    )}

                    {/* 슬라이드 네비게이션 */}
                    <div className="flex items-center justify-center space-x-4 mt-4">
                      <button
                        onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                        disabled={currentSlide === 0}
                        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <span className="text-sm text-gray-600">
                        {currentSlide + 1} / {pptData.slides.length}
                      </span>
                      <button
                        onClick={() => setCurrentSlide(Math.min(pptData.slides.length - 1, currentSlide + 1))}
                        disabled={currentSlide === pptData.slides.length - 1}
                        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 네비게이션 버튼 */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrev}
          disabled={currentStep === 'select-proposal'}
          className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          이전
        </button>
        
        {currentStep !== 'preview' && currentStep !== 'generate' && (
          <button
            onClick={handleNext}
            disabled={
              (currentStep === 'select-proposal' &&
                (!selectedProposal || !selectedAuthorizationId)) ||
              (currentStep === 'select-template' && !selectedTemplate)
            }
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            다음
            <ChevronRight className="h-5 w-5 ml-1" />
          </button>
        )}

        {currentStep === 'preview' && (
          <button
            onClick={handleDownload}
            disabled={isDownloading || !selectedAuthorizationId}
            className="flex items-center px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {isDownloading ? (
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            ) : (
              <Check className="h-5 w-5 mr-2" />
            )}
            저장 및 다운로드
          </button>
        )}
      </div>
    </div>
  );
};

export default PPTGenerator;
